import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { VngIconButtonComponent, VngIconComponent } from "@vengreso/angular-components";
import { formatDistanceToNow } from "date-fns";

import { AppNotificationService } from "src/app/core/core-services/services/app-notification.service";
import { AppNotification, NotificationCategory } from "src/app/core/models/notification.model";
import { resolveNotificationTemplateRenderer } from "../templates/notification-template-registry";
import { NotificationActionsRendererComponent } from "../templates/notification-actions-renderer/notification-actions-renderer.component";
import { NotificationImageRendererComponent } from "../templates/notification-image-renderer/notification-image-renderer.component";
import { NotificationLinkRendererComponent } from "../templates/notification-link-renderer/notification-link-renderer.component";
import { NotificationSurveyRendererComponent } from "../templates/notification-survey-renderer/notification-survey-renderer.component";
import { NotificationTextRendererComponent } from "../templates/notification-text-renderer/notification-text-renderer.component";
import { NotificationVideoRendererComponent } from "../templates/notification-video-renderer/notification-video-renderer.component";

export const NOTIFICATION_CATEGORY_UI: Record<NotificationCategory, { icon: string; label: string }> = {
  payments: { icon: "credit_card", label: "Payments" },
  invitations: { icon: "mail", label: "Invitations" },
  rewards: { icon: "emoji_events", label: "Rewards" },
  marketing: { icon: "campaign", label: "Marketing" },
  sharing: { icon: "share", label: "Sharing" },
  promotions: { icon: "sell", label: "Promotions" },
  gifts: { icon: "redeem", label: "Gifts" },
};

/**
 * A single row in the notification panel: read/unread state, a relative
 * timestamp, a dismiss control, and the template-specific body delegated to
 * the registry's renderer (`notification-template-registry.ts`, unknown
 * kinds fall back to text). Talks to `AppNotificationService` directly
 * (all its mutators are already gated + optimistic) rather than bubbling
 * events through the panel.
 */
@Component({
  selector: "app-notification-item",
  standalone: true,
  imports: [
    CommonModule,
    VngIconButtonComponent,
    VngIconComponent,
    NotificationTextRendererComponent,
    NotificationLinkRendererComponent,
    NotificationImageRendererComponent,
    NotificationVideoRendererComponent,
    NotificationActionsRendererComponent,
    NotificationSurveyRendererComponent,
  ],
  templateUrl: "./notification-item.component.html",
  styleUrls: ["./notification-item.component.scss"],
})
export class NotificationItemComponent {
  @Input() notification!: AppNotification;

  constructor(private readonly appNotificationService: AppNotificationService) {}

  get isUnread(): boolean {
    return !this.notification.read_at;
  }

  get categoryUi(): { icon: string; label: string } {
    return NOTIFICATION_CATEGORY_UI[this.notification.category];
  }

  get relativeTime(): string {
    if (!this.notification.created_at) {
      return "";
    }
    return formatDistanceToNow(new Date(this.notification.created_at * 1000), { addSuffix: true });
  }

  /** The renderer kind to mount — resolved through the registry so unknown kinds fall back to `text`. */
  get rendererKind(): string {
    return resolveNotificationTemplateRenderer(this.notification.template) === NotificationTextRendererComponent
      ? "text"
      : this.notification.template.kind;
  }

  onRowClick(): void {
    if (this.isUnread) {
      this.appNotificationService.markRead(this.notification.id);
    }
  }

  onDismissClick(event: Event): void {
    event.stopPropagation();
    this.appNotificationService.dismiss(this.notification.id);
  }

  onRespond(payload: Record<string, unknown>): void {
    this.appNotificationService.respond(this.notification.id, payload);
  }
}
